import { AppEnvs } from 'types/envs'

/**
 * Direct S3 links bypass CDN caching and inflate S3 egress costs (BR-14229).
 * These helpers rewrite such links in saved content to their CDN equivalents.
 */

const CATALOG_CDN_URL = 'https://cdn.boosterprep.com/catalog'

const CATALOG_BUCKETS = [
  'datbooster',
  'patbooster',
  'organic-chemistry',
  'oatbooster',
  'inbdebooster',
  'ndebbooster',
  'nbdhebooster',
  'quantitative-reasoning',
  'mcatbooster',
  'reading-comprehension',
]

/**
 * REACT_APP_ENV -> the user-media bucket served by that env's REACT_APP_IMAGEKIT_URL
 */
const USER_MEDIA_BUCKETS: Record<AppEnvs, string> = {
  [AppEnvs.DEV]: 'booster-prep-user-media-dev',
  [AppEnvs.QA]: 'booster-prep-user-media-qa',
  [AppEnvs.SANDBOX]: 'booster-prep-user-media-sandbox',
  [AppEnvs.PROD]: 'booster-prep-user-media-production',
}

const USER_MEDIA_BUCKET: string | undefined = USER_MEDIA_BUCKETS[process.env.REACT_APP_ENV]

/**
 * Matches the S3 virtual-hosted host part, with or without a region, e.g.
 * ".s3.amazonaws.com/", ".s3.us-east-2.amazonaws.com/" or ".s3-us-east-2.amazonaws.com/"
 */
const S3_HOST_SUFFIX = String.raw`\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com/`

const CATALOG_BUCKET_URL_REGEX = new RegExp(String.raw`https?://(${CATALOG_BUCKETS.join('|')})${S3_HOST_SUFFIX}`, 'g')

const USER_MEDIA_BUCKET_URL_REGEX = new RegExp(String.raw`https?://${USER_MEDIA_BUCKET}${S3_HOST_SUFFIX}`, 'g')

/**
 * Replace direct S3 URLs in a text (e.g. editor HTML) with CDN URLs:
 * - catalog buckets (CMS content media) -> CloudFront, e.g.
 *   "https://datbooster.s3.amazonaws.com/a.png" -> "https://cdn.boosterprep.com/catalog/datbooster/a.png"
 * - the current env's user media bucket (blog posts, uploads) -> REACT_APP_IMAGEKIT_URL, e.g. on dev
 *   "https://booster-prep-user-media-dev.s3.us-east-2.amazonaws.com/a.png" -> "https://ik.imagekit.io/1tgmhvb9t/dev/a.png"
 */
export const replaceS3UrlsWithCdn = (text: string) => {
  if (!text) {
    return text
  }

  const cdnText = text.replace(CATALOG_BUCKET_URL_REGEX, (_, bucket: string) => `${CATALOG_CDN_URL}/${bucket}/`)

  if (!USER_MEDIA_BUCKET || !process.env.REACT_APP_IMAGEKIT_URL) {
    return cdnText
  }

  return cdnText.replace(USER_MEDIA_BUCKET_URL_REGEX, () => `${process.env.REACT_APP_IMAGEKIT_URL}/`)
}
